import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import { Pill } from "../components/common/Primitives";
import { ChevronField, Comprehensive, Clarity, Knowledge, Network, Idea } from "../components/common/BrandShapes";
import { ShieldCheck, Sparkles, Lock, Users, Landmark, Handshake, TrendingUp, DoorOpen, FileSearch, BadgeCheck, ArrowRight } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

export default function ForInvestors() {
  const [stats, setStats] = useState({ total_startups: 0, total_investors: 0, total_interests: 0, total_funding_demand_cr: 0 });
  useEffect(() => { api.get("/marketplace/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen text-ink" data-testid="for-investors-page">
      <Seo
        title="For Investors | Curated Deal Flow on VenturaZ"
        description="Meet vetted, high-signal startups on VenturaZ. AI-ranked matches, structured due diligence and secure deal rooms — built for angels, VCs and family offices."
        path="/for-investors"
      />
      <PublicNavbar />

      {/* HERO — investor-tilted: signal, not spam */}
      <section className="relative brand-section-primary overflow-hidden" data-testid="investor-hero">
        <div className="absolute inset-y-0 left-0 w-[50%] pointer-events-none" aria-hidden="true">
          <ChevronField color="#FFFFFF" opacity={0.20} className="w-full h-full" />
        </div>
        <Comprehensive size={200} color="#FFFFFF" opacity={0.15} strokeWidth={1.4}
          className="absolute top-16 right-10 hidden md:block" />
        <Knowledge size={140} color="#FFD700" opacity={0.28} strokeWidth={1.4}
          className="absolute bottom-16 right-[38%] hidden md:block" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-24 lg:pt-32 lg:pb-28 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-white/80 mb-4" data-testid="investor-hero-overline">
                VENTURAZ · FOR CAPITAL PROVIDERS
              </div>
              <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] drop-shadow-[0_2px_16px_rgba(0,0,0,0.15)]" data-testid="investor-hero-headline">
                Signal,<br/><span className="text-gold">not spam.</span>
              </h1>
              <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="investor-hero-subheadline">
                A curated flow of KYC-verified, admin-reviewed startups — filtered by your mandate,
                enriched with AI reasoning, and delivered through structured deal rooms with
                controlled data access. Built for family offices, AIFs, VCs, PE, NBFCs and banks.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/investor/join" className="px-6 py-3 bg-gold text-navy font-heading font-semibold hover:bg-gold-hover transition-all shadow-[0_10px_30px_rgba(255,215,0,0.35)] rounded-md" data-testid="investor-hero-join-cta">
                  Join as Investor →
                </Link>
                <Link to="/marketplace" className="px-6 py-3 bg-white/10 backdrop-blur-md border border-white/25 text-white font-heading hover:bg-white/20 transition-all rounded-md" data-testid="investor-hero-preview-cta">
                  Preview the Marketplace
                </Link>
                <Link to="/pricing" className="px-6 py-3 text-white/85 font-heading hover:text-gold transition-colors" data-testid="investor-hero-pricing-cta">
                  Investor Plans →
                </Link>
              </div>
              <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono text-white/75 uppercase tracking-wider">
                <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-gold" /> KYC-verified startups</span>
                <span className="flex items-center gap-2"><Lock size={14} className="text-gold" /> Controlled data access</span>
                <span className="flex items-center gap-2"><Handshake size={14} className="text-gold" /> Advisor-led deal rooms</span>
              </div>
            </motion.div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="bg-white/95 backdrop-blur-md border border-white/40 rounded-lg p-6 shadow-[0_24px_60px_rgba(0,40,30,0.25)]" data-testid="investor-hero-mandate-card">
              <div className="flex items-center justify-between mb-4">
                <div className="overline">Your Investment Mandate</div>
                <Pill tone="gold">CURATED</Pill>
              </div>
              <div className="space-y-3 text-sm">
                <MandateRow label="Ticket Range" value="₹1 Cr – ₹100 Cr" />
                <MandateRow label="Stage Focus" value="Seed to Series C" />
                <MandateRow label="Sectors" value="Your pick — 24+ available" />
                <MandateRow label="Deal Types" value="Equity · Debt · Hybrid · CB" />
              </div>
              <div className="hairline my-5" />
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <div className="text-3xl font-heading text-ink" data-testid="investor-stats-startups">{stats.total_startups}</div>
                  <div className="overline mt-1">Verified Startups</div>
                </div>
                <div>
                  <div className="text-3xl font-heading text-ink">₹{stats.total_funding_demand_cr}<span className="text-lg"> Cr</span></div>
                  <div className="overline mt-1">Live Demand</div>
                </div>
              </div>
              <Link to="/investor/join" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white font-heading text-sm hover:bg-navy/90 transition-colors rounded-md" data-testid="investor-mandate-cta">
                Configure your mandate <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
        <div className="brand-wave-bottom" aria-hidden="true" />
      </section>

      {/* THREE PILLARS — Curation, Access, Workflow */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="investor-pillars">
        <div className="overline mb-3">HOW VENTURAZ SERVES YOU</div>
        <h2 className="font-display text-4xl md:text-5xl text-ink max-w-3xl mb-14">Three pillars, one workspace.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PillarCard
            icon={FileSearch}
            title="Curation you can trust"
            body="Every startup submits KYC, funding profile, and diligence-ready documents. Our team reviews, tiers and tags each one before it enters your feed."
            testid="pillar-curation"
          />
          <PillarCard
            icon={Sparkles}
            title="AI-reasoned matching"
            body="Claude Sonnet 4.5 explains every high-score match in plain English — sector fit, stage alignment, ticket compatibility, and cap-table health."
            testid="pillar-ai-matching"
          />
          <PillarCard
            icon={DoorOpen}
            title="End-to-end deal rooms"
            body="From NDA to LOI to Term Sheet to Definitive Docs — one workspace, e-signature ready, with document versioning, due diligence tracking, and a dedicated VenturaZ manager."
            testid="pillar-deal-rooms"
          />
        </div>
      </section>

      {/* TIERED VISIBILITY — Retail, HNI, Institutional */}
      <section className="relative brand-section-primary overflow-hidden" data-testid="investor-tiers-section">
        <div className="absolute inset-y-0 left-0 w-full pointer-events-none opacity-30" aria-hidden="true">
          <ChevronField color="#FFFFFF" opacity={0.15} className="w-full h-full" />
        </div>
        <Network size={220} color="#FFFFFF" opacity={0.10} className="absolute top-14 right-8 hidden lg:block" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
          <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-white/75 mb-3">TIERED ACCESS</div>
          <h2 className="font-display text-white text-4xl md:text-5xl max-w-3xl mb-4">Data access scales with your mandate.</h2>
          <p className="text-white/80 max-w-2xl mb-12">
            Retail angels see the basics. HNIs and family offices unlock financials.
            Institutional investors get full cap tables, MIS, unit economics, and DD reports.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TierCard tone="basic" label="Tier 1 · Retail / Angel" perks={[
              "Startup profile & pitch",
              "Sector, stage, ask, city",
              "Founder public bio",
            ]} />
            <TierCard tone="mid" label="Tier 2 · HNI / Family Office" perks={[
              "Tier 1 access",
              "Historical revenue & margins",
              "Round structure & terms",
              "Cap-table summary",
            ]} />
            <TierCard tone="premium" label="Tier 3 · Institutional" perks={[
              "Tier 2 access",
              "Detailed MIS & unit economics",
              "Full cap table",
              "Free due-diligence add-on",
              "Priority deal room access",
            ]} highlight />
          </div>
        </div>
        <div className="brand-wave-bottom" aria-hidden="true" />
      </section>

      {/* WORKFLOW — 4-step journey */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="investor-workflow">
        <div className="overline mb-3">FROM ONBOARDING TO CLOSE</div>
        <h2 className="font-display text-4xl md:text-5xl text-ink max-w-3xl mb-14">Your journey on VenturaZ.</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <WorkflowStep n="01" title="Join & KYC" body="Sign up, complete investor KYC, configure your mandate. VenturaZ approves within 24 hours." />
          <WorkflowStep n="02" title="Discover" body="Browse curated startups, review AI match reasons, save shortlists, and unlock full profiles on interest." />
          <WorkflowStep n="03" title="Query & Diligence" body="Send predefined queries via structured workflow. Optional Diligence Add-on for a full third-party DD report." />
          <WorkflowStep n="04" title="Deal Room & Close" body="Move to a dedicated deal room. NDA → LOI → Term Sheet → Definitive Docs, all e-signable inside VenturaZ." />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 border-t border-navy/10" data-testid="investor-trust-strip">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          <TrustPoint icon={ShieldCheck} title="Regulator-first design" body="VenturaZ is a curated facilitation platform, not a securities exchange or crowdfunding site." />
          <TrustPoint icon={BadgeCheck} title="Verified counterparties" body="No anonymous founders, no ghost pitches. Every party is identity-checked and role-tagged." />
          <TrustPoint icon={Users} title="Dedicated VenturaZ manager" body="A named human owns your workspace end-to-end — no support queue, no ticket triage." />
          <TrustPoint icon={TrendingUp} title="Data-dense workspace" body="MIS, cap tables, DD reports, deal-stage tracking — all in one workspace, no PDF hunts." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="investor-final-cta">
        <div className="relative rounded-2xl overflow-hidden brand-section-primary p-12 md:p-16">
          <Idea size={180} color="#FFD700" opacity={0.30} className="absolute -top-6 -right-6 hidden md:block" />
          <div className="relative max-w-3xl">
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-white/75 mb-3">READY TO START?</div>
            <h2 className="font-display text-white text-4xl md:text-5xl leading-tight mb-6">
              Join a marketplace where <span className="text-gold">signal matters more than volume.</span>
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              We onboard investors in under 24 hours. Configure your mandate, and start seeing
              curated startups matched to your thesis this week.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/investor/join" className="px-7 py-3.5 bg-gold text-navy font-heading font-semibold hover:bg-gold-hover transition-all shadow-[0_10px_30px_rgba(255,215,0,0.35)] rounded-md" data-testid="investor-cta-join">
                Join as Investor →
              </Link>
              <Link to="/pricing" className="px-7 py-3.5 bg-white/10 border border-white/25 text-white font-heading hover:bg-white/20 transition-all rounded-md" data-testid="investor-cta-pricing">
                Compare Investor Plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MandateRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-navy/8 pb-2 last:border-0">
      <span className="text-ink-muted text-xs font-mono uppercase tracking-wider">{label}</span>
      <span className="text-ink font-heading">{value}</span>
    </div>
  );
}

function PillarCard({ icon: Icon, title, body, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
      className="border border-navy/10 rounded-lg p-8 bg-white/70 backdrop-blur hover:border-gold hover:-translate-y-1 transition-all"
      data-testid={testid}
    >
      <Icon className="text-gold mb-4" size={26} />
      <div className="font-heading text-ink text-xl mb-3">{title}</div>
      <div className="text-ink-muted text-sm leading-relaxed">{body}</div>
    </motion.div>
  );
}

function TierCard({ tone, label, perks, highlight }) {
  return (
    <div className={`rounded-lg p-7 backdrop-blur-md ${highlight ? "bg-gold/95 text-navy border border-gold" : "bg-white/10 border border-white/20 text-white"}`}>
      <div className={`text-[10px] font-mono uppercase tracking-widest mb-2 ${highlight ? "text-navy/70" : "text-white/70"}`}>{tone.toUpperCase()}</div>
      <div className="font-heading text-xl mb-4">{label}</div>
      <ul className="space-y-2 text-sm">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className={`inline-block w-1 h-1 rounded-full mt-2 flex-shrink-0 ${highlight ? "bg-navy" : "bg-gold"}`} />
            <span className={highlight ? "text-navy/90" : "text-white/90"}>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WorkflowStep({ n, title, body }) {
  return (
    <div className="relative border-l-2 border-gold pl-6 pb-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-gold mb-2">STEP {n}</div>
      <div className="font-heading text-ink text-lg mb-2">{title}</div>
      <div className="text-ink-muted text-sm leading-relaxed">{body}</div>
    </div>
  );
}

function TrustPoint({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-gold/15 border border-gold/40 mb-4">
        <Icon className="text-gold" size={20} />
      </div>
      <div className="font-heading text-ink text-base mb-1">{title}</div>
      <div className="text-ink-muted text-xs leading-relaxed">{body}</div>
    </div>
  );
}
